import type { ChangeEvent } from "react"

import { Button } from "@/components/button"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Input } from "@/components/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover"
import { SettingsIcon } from "@/domains/icons"
import { cn } from "@/domains/library"

/**
 * Preset slippage values (in %). The first few mirror the swap-settings
 * defaults so the feel is consistent across the app.
 */
const SLIPPAGE_PRESETS = [0.1, 0.5, 1, 3] as const

const SLIPPAGE_MIN = 0.01
const SLIPPAGE_MAX = 10
const SLIPPAGE_WARN_HIGH = 5

const DEADLINE_MIN = 1
const DEADLINE_MAX = 4320

export type ManageSettingsPopoverViewProps = {
  /** Current slippage, in percent (e.g. `1` = 1%). */
  slippage: number
  onSlippageChange: (slippage: number) => void
  /** Current deadline, in minutes from now. */
  deadlineMinutes: number
  onDeadlineMinutesChange: (minutes: number) => void
  /** Optional className for the trigger button. */
  className?: string
}

/**
 * Pure view for the manage-position transaction-settings popover.
 *
 * Mirrors Uniswap v3's "gear icon → slippage + deadline" affordance but uses
 * our Radix Popover primitive. Composites wire this up to the shared
 * `ManagePositionStoreBase.slippage/deadline` surface so all four manage
 * stores (increase / decrease / collect / burn) stay in sync.
 */
export const ManageSettingsPopoverView = ({
  slippage,
  onSlippageChange,
  deadlineMinutes,
  onDeadlineMinutesChange,
  className,
}: ManageSettingsPopoverViewProps) => {
  const slippageOutOfRange =
    !Number.isFinite(slippage) || slippage < SLIPPAGE_MIN || slippage > SLIPPAGE_MAX
  const slippageHigh = slippage >= SLIPPAGE_WARN_HIGH && !slippageOutOfRange
  const deadlineOutOfRange =
    !Number.isFinite(deadlineMinutes) ||
    deadlineMinutes < DEADLINE_MIN ||
    deadlineMinutes > DEADLINE_MAX

  const handleSlippageInput = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === "") {
      onSlippageChange(NaN)
      return
    }
    if (!/^\d*(?:\.\d*)?$/.test(raw)) return
    const next = Number(raw)
    if (Number.isNaN(next)) return
    onSlippageChange(next)
  }

  const handleDeadlineInput = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === "") {
      onDeadlineMinutesChange(NaN)
      return
    }
    if (!/^\d+$/.test(raw)) return
    const next = Number(raw)
    if (Number.isNaN(next)) return
    onDeadlineMinutesChange(next)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="neutral"
          size="sm"
          aria-label="Transaction settings"
          className={cn("h-9 w-9 px-0", className)}
        >
          <SettingsIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="flex flex-col gap-4">
          <div className="text-t100 text-sm font-medium">Transaction settings</div>

          <div className="flex flex-col gap-2">
            <div className="text-t300 text-xs">Max slippage</div>
            <InnerCard hasWarning={slippageOutOfRange || slippageHigh}>
              <div className="grid grid-cols-5 gap-2">
                {SLIPPAGE_PRESETS.map((preset) => (
                  <Button
                    key={preset}
                    type="button"
                    variant={Number.isFinite(slippage) && slippage === preset ? "primary" : "neutral"}
                    className="h-8 px-0 text-xs"
                    onClick={() => onSlippageChange(preset)}
                  >
                    {preset}%
                  </Button>
                ))}
                <div className="relative">
                  <Input
                    aria-label="Custom slippage percent"
                    inputMode="decimal"
                    pattern="[0-9]*"
                    placeholder="Custom"
                    value={Number.isFinite(slippage) ? String(slippage) : ""}
                    onChange={handleSlippageInput}
                    className="h-8 pr-6 text-right text-xs"
                  />
                  <span className="text-t300 pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs">
                    %
                  </span>
                </div>
              </div>
              <ShowWhenTrue when={slippageOutOfRange}>
                <InnerCardWarning
                  warning={`Slippage must be between ${SLIPPAGE_MIN}% and ${SLIPPAGE_MAX}%`}
                />
              </ShowWhenTrue>
              <ShowWhenTrue when={slippageHigh}>
                <InnerCardWarning warning="High slippage tolerance increases the risk of an unfavourable trade." />
              </ShowWhenTrue>
            </InnerCard>
          </div>

          <div className="flex flex-col gap-2">
            <div className="text-t300 text-xs">Transaction deadline</div>
            <InnerCard hasWarning={deadlineOutOfRange}>
              <div className="flex items-center justify-between gap-2">
                <Input
                  aria-label="Transaction deadline minutes"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="20"
                  value={Number.isFinite(deadlineMinutes) ? String(deadlineMinutes) : ""}
                  onChange={handleDeadlineInput}
                  className="h-8 w-24 text-right text-xs"
                />
                <span className="text-t300 text-xs">minutes</span>
              </div>
              <ShowWhenTrue when={deadlineOutOfRange}>
                <InnerCardWarning
                  warning={`Deadline must be between ${DEADLINE_MIN} and ${DEADLINE_MAX} minutes`}
                />
              </ShowWhenTrue>
            </InnerCard>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
